'use client'

import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import React from 'react'

import { cn } from '../../utils/cn'
import { Loading } from '../Loading'

export type ButtonVariant =
  | 'primary'
  | 'primary-outlined'
  | 'primary-ghost'
  | 'primary-text'
  | 'brand'
  | 'brand-outlined'
  | 'brand-text'
  | 'warning'
  | 'inverted'

export type ButtonSize = 'sm' | 'md' | 'lg'

const iconSizeMap = {
  sm: 16,
  md: 20,
  lg: 24,
}

const loadingIconSizeMap = {
  sm: iconSizeMap.sm * 3,
  md: iconSizeMap.md * 3,
  lg: iconSizeMap.lg * 3,
}

export const buttonVariants = cva(
  'relative box-border inline-flex select-none items-center justify-center overflow-hidden rounded-sm border-none bg-transparent text-center no-underline transition-all duration-200 ease-in-out disabled:cursor-default',
  {
    variants: {
      variant: {
        primary:
          'border border-solid border-blue-01 bg-blue-01 text-white disabled:border-gray-05 disabled:bg-gray-05 disabled:text-gray-03',
        'primary-outlined':
          'border border-solid border-blue-01 bg-white text-blue-01 hover:bg-blue-01 hover:text-white disabled:border-gray-04 disabled:bg-white disabled:text-gray-04 disabled:hover:bg-white disabled:hover:text-gray-04',
        'primary-ghost':
          'border border-solid border-blue-01 text-blue-01 hover:border-blue-01 disabled:border-gray-04 disabled:text-gray-04',
        'primary-text':
          'text-blue-01 disabled:bg-gray-05 disabled:text-gray-03',
        brand:
          'relative z-0 border border-solid border-transparent text-white before:absolute before:-inset-px before:-z-10 before:rounded-sm before:bg-gradient-primary before:transition-all before:duration-200 after:absolute after:-inset-px after:-z-20 after:rounded-sm after:bg-red-01 after:transition-all hover:before:opacity-0 disabled:bg-gray-05 disabled:text-gray-03 disabled:before:opacity-0 disabled:after:opacity-0',
        'brand-outlined':
          'relative z-0 border border-solid border-red-01 bg-white text-red-01 before:absolute before:-inset-px before:-z-10 before:rounded-sm before:bg-gradient-primary before:opacity-0 before:transition-opacity before:duration-200 hover:border-transparent hover:bg-transparent hover:text-white hover:before:opacity-100 disabled:border-gray-04 disabled:bg-gray-05 disabled:text-gray-03 disabled:hover:before:opacity-0',
        'brand-text': 'text-red-01 disabled:bg-gray-05 disabled:text-gray-03',
        warning:
          'border border-solid border-warning bg-warning text-white disabled:border-gray-05 disabled:bg-gray-05 disabled:text-gray-03',
        inverted:
          'rounded-sm border border-solid border-white text-white hover:opacity-80 disabled:border-gray-04 disabled:text-gray-04 disabled:hover:opacity-100',
      },
      size: {
        sm: 'gap-1 px-[7px] py-[5px] typography-button3 [&_svg]:size-4',
        md: 'gap-2 px-[15px] py-[9px] typography-button2 [&_svg]:size-5',
        lg: 'gap-2 px-[23px] py-[15px] typography-button1 [&_svg]:size-6',
      },
      rounded: {
        true: 'rounded-full before:rounded-full after:rounded-full',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
)

interface BaseButtonProps {
  /**
   * Class name
   */
  className?: string

  /**
   * Button variant
   */
  variant?: ButtonVariant

  /**
   * Size of button
   */
  size?: 'sm' | 'md' | 'lg'

  /**
   * Button type
   */
  type?: 'button' | 'submit' | 'reset'

  /**
   * Content of button
   */
  children?: React.ReactNode

  /**
   * Rounded button
   */
  rounded?: boolean

  /**
   * Button with full width
   */
  fullWidth?: boolean

  /**
   * Loading state
   */
  loading?: boolean

  /**
   * Disabled state
   */
  disabled?: boolean
}

export type ButtonProps = BaseButtonProps &
  React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }

/**
 * Primary UI component for user interaction
 */
export const Button = ({
  className,
  variant,
  size = 'md',
  rounded,
  fullWidth,
  asChild = false,
  loading,
  children,
  ...props
}: ButtonProps) => {
  const Comp = asChild ? Slot : 'button'

  if (loading) {
    return (
      <div
        className={cn(
          buttonVariants({ variant, size, rounded, fullWidth, className }),
          'pointer-events-none cursor-pointer',
        )}
      >
        <div className="invisible">{children}</div>
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 [&_path]:fill-current">
          <div
            className="flex items-center justify-center"
            style={{
              height: loadingIconSizeMap[size],
              width: loadingIconSizeMap[size],
            }}
          >
            <Loading />
          </div>
        </div>
      </div>
    )
  }

  return (
    <Comp
      data-slot="button"
      className={cn(
        buttonVariants({ variant, size, rounded, fullWidth, className }),
      )}
      style={
        {
          ...props.style,
        } as React.CSSProperties
      }
      {...props}
    >
      {children}
    </Comp>
  )
}
